import { CARD } from './tableLayout';
import DataTable from './DataTable';
import { competitorColumns } from './CompetitorsReport';
import type { BoardroomKindRow, BoardroomRelated } from '../../../types';

/**
 * What the room turned up that is not the subject — and where you sort it.
 *
 * A room hunting Utah car washes ends up holding janitorial suppliers, window
 * washers and vinyl wrappers. Not by accident: it searched, it found
 * neighbours, and it filed them honestly. The drift is not in the rows, it is
 * in the KINDS — room 30 invented "Car Wash Chemicals" and then faithfully
 * filled it twenty products deep.
 *
 * So the judgement is made once per kind, here, against the counts. Five
 * decisions instead of a hundred and fifty, and stable as the room grows.
 *
 * NOTHING IS DELETED. A janitorial supplier is the best lead in the room for
 * somebody asking a different question; setting its kind aside moves it off
 * the tables that answer THIS question and onto one that says so. It comes
 * back in a click.
 */
function Kind({
  row,
  onSet,
  busy,
}: {
  row: BoardroomKindRow;
  onSet: (relevance: 'core' | 'related') => void;
  busy: boolean;
}) {
  const aside = row.relevance === 'related';
  return (
    <div
      className={`flex items-start gap-3 rounded-md border px-3 py-2 ${
        aside
          ? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40'
          : 'border-gray-200 dark:border-gray-700'
      }`}
      data-testid={`kind-${row.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`truncate text-xs font-medium ${
              aside
                ? 'text-gray-500 dark:text-gray-400'
                : 'text-gray-900 dark:text-gray-100'
            }`}
          >
            {row.title}
          </span>
          {aside && (
            <span className="shrink-0 rounded bg-gray-100 px-1 text-[11px] text-gray-500 dark:bg-gray-700 dark:text-gray-300">
              set aside
            </span>
          )}
        </div>
        {/* THE COUNTS ARE THE ARGUMENT. "Twenty products deep" is what tells
            you a kind has quietly become a second research project. */}
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {row.products} product{row.products === 1 ? '' : 's'} ·{' '}
          {row.companies} compan{row.companies === 1 ? 'y' : 'ies'}
          {row.body ? ` · ${row.body.slice(0, 90)}` : ''}
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => onSet(aside ? 'core' : 'related')}
        title={
          aside
            ? 'Put this kind back on the tables, and back in the work queue'
            : 'Move this kind off the tables that answer this room’s question. Nothing is deleted.'
        }
        className="shrink-0 whitespace-nowrap rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
      >
        {aside ? 'Part of this room' : 'Set aside'}
      </button>
    </div>
  );
}

export default function RelatedReport({
  data,
  onOpen,
  onSet,
  busy = false,
  roomName,
}: {
  data: BoardroomRelated;
  onOpen: (itemId: number) => void;
  onSet: (itemId: number, relevance: 'core' | 'related') => void;
  busy?: boolean;
  roomName?: string;
}) {
  const aside = data.kinds.filter(k => k.relevance === 'related');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className={CARD}>
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          Every kind of thing this room has found. Setting one aside takes it off the
          other tables and out of the work queue - nothing is deleted, and it comes back
          in a click.
        </p>
        <div className="space-y-1.5">
          {data.kinds.map(k => (
            <Kind key={k.id} row={k} busy={busy} onSet={r => onSet(k.id, r)} />
          ))}
          {data.kinds.length === 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              The room has not sorted what it found into kinds yet, so there is nothing
              to set aside.
            </div>
          )}
        </div>
      </div>

      {aside.length > 0 && (
        <div className={`${CARD} min-h-0 flex-1`}>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">
            Off the tables - {data.rows.length} to look at another time
          </div>
          <DataTable
            table="related"
            room={roomName}
            columns={competitorColumns(onOpen)}
            rows={data.rows}
            rowKey={r => r.id}
            testId="related-rows"
            empty="Nothing is filed under the kinds you set aside."
          />
        </div>
      )}
    </div>
  );
}
